import { BOSSES } from './bosses.js';
import { ACT2_BOSSES } from './act2Bosses.js';
import { ACT3_BOSSES } from './act3Bosses.js';
import { ACTS, isActUnlocked } from './acts.js';
import { FUSION_DEFS } from './fusions.js';
import { CHARACTERS } from './characters.js';

/**
 * Achievements (BULLET-HEAVEN.md §H6 — machine #8, the breadcrumb trail).
 *
 * The point is not the list. The point is that there is ALWAYS a named next
 * thing one or two runs away. Survivor already recorded statistics; it never
 * *asked* the player for anything, so a run that didn't clear a boss produced
 * no visible progress at all. That's the difference between a loss that ends a
 * session and a loss that ends a run.
 *
 * Shape of an entry:
 *
 *   id          stable string; what's persisted. Never renamed — a rename
 *               silently re-locks the achievement for everyone who had it.
 *   name        short, imperative or declarative. Shown in the list.
 *   hint        what to do, in one line. Visible while locked; this is the
 *               breadcrumb, so it must be actionable, not cryptic.
 *   echoes      payout on unlock. Scaled to effort, and deliberately modest:
 *               these are a reason to try something, not an economy.
 *   group       for grouping in the UI only.
 *   check(ctx)  ctx is { stats, run } — lifetime statistics and the run that
 *               just ended (null when re-evaluating outside a run, e.g. on
 *               opening the menu). A check MUST tolerate run === null and it
 *               MUST tolerate missing fields on old saves: every numeric read
 *               goes through `n()` below.
 *
 * Achievements are evaluated once at run end and again whenever the list is
 * shown, so a lifetime-total achievement unlocks even if the run that crossed
 * the threshold predates the achievement existing. That is why almost every
 * check reads `stats` rather than `run` where it can.
 */

/** Missing-field-tolerant numeric read. Old saves simply lack newer counters. */
const n = (value) => (typeof value === 'number' && Number.isFinite(value) ? value : 0);

/** A run-scoped check that must not fire when there is no run in hand. */
const inRun = (fn) => ({ run }) => Boolean(run) && fn(run);

/** Builds a "reach N of a lifetime counter" ladder — the bulk of the list. */
function ladder({ idPrefix, name, hint, group, field, tiers }) {
  return tiers.map(([threshold, echoes]) => ({
    id: `${idPrefix}:${threshold}`,
    name: `${name} ${threshold.toLocaleString()}`,
    hint: hint(threshold),
    echoes,
    group,
    check: ({ stats }) => n(stats[field]) >= threshold,
  }));
}

export const ACHIEVEMENTS = [
  // --- getting started ----------------------------------------------------
  {
    id: 'firstRun',
    name: 'First Voice',
    hint: 'Finish a run, any run.',
    echoes: 25,
    group: 'Beginnings',
    check: ({ stats }) => n(stats.runs) >= 1,
  },
  {
    id: 'firstBoss',
    name: 'One Down',
    hint: 'Defeat any boss.',
    echoes: 40,
    group: 'Beginnings',
    check: ({ stats }) => n(stats.bossesDefeated) >= 1,
  },
  {
    id: 'firstWin',
    name: 'Encore',
    hint: 'Clear the gauntlet.',
    echoes: 150,
    group: 'Beginnings',
    check: ({ stats }) => n(stats.wins) >= 1,
  },

  // --- the microloop (BULLET-HEAVEN.md §H1) -------------------------------
  ...ladder({
    idPrefix: 'motes',
    name: 'Gleaner',
    hint: (t) => `Collect ${t.toLocaleString()} XP motes.`,
    group: 'The Gleaning',
    field: 'totalMotesCollected',
    tiers: [[500, 20], [2500, 40], [10000, 80], [50000, 160], [200000, 320]],
  }),
  {
    id: 'moteTorrent',
    name: 'Torrent',
    hint: 'Collect 60 motes in a single moment.',
    echoes: 60,
    group: 'The Gleaning',
    check: ({ stats }) => n(stats.bestMoteBurst) >= 60,
  },
  {
    id: 'moteFlood',
    name: 'Flood Tide',
    hint: 'Collect 150 motes in a single moment.',
    echoes: 140,
    group: 'The Gleaning',
    check: ({ stats }) => n(stats.bestMoteBurst) >= 150,
  },

  // --- skill expression ---------------------------------------------------
  ...ladder({
    idPrefix: 'graze',
    name: 'Near Miss',
    hint: (t) => `Graze ${t.toLocaleString()} bullets.`,
    group: 'Close Work',
    field: 'totalGrazes',
    tiers: [[100, 20], [500, 40], [2500, 80], [10000, 160], [40000, 320]],
  }),
  {
    id: 'grazeRun500',
    name: 'Brinkmanship',
    hint: 'Graze 500 bullets in one run.',
    echoes: 90,
    group: 'Close Work',
    check: inRun((run) => n(run.grazes) >= 500),
  },
  {
    id: 'untouchedBoss',
    name: 'Not a Scratch',
    hint: 'Defeat a boss without taking a single hit that run.',
    echoes: 200,
    group: 'Close Work',
    check: inRun((run) => n(run.bossesDefeated) >= 1 && n(run.damageTaken) === 0),
  },
  {
    id: 'noPods',
    name: 'Unaided',
    hint: 'Clear three bosses in one run without collecting a health pod.',
    echoes: 120,
    group: 'Close Work',
    check: inRun((run) => n(run.bossesDefeated) >= 3 && n(run.podsCollected) === 0),
  },

  // --- Harmonics (BULLET-HEAVEN.md §H4) -----------------------------------
  {
    id: 'firstFusion',
    name: 'Harmonize',
    hint: 'Fuse two weapons into a Harmonic.',
    echoes: 100,
    group: 'Harmonics',
    check: ({ stats }) => n(stats.totalFusions) >= 1,
  },
  {
    id: 'doubleFusion',
    name: 'Two-Part Invention',
    hint: 'Reach two Harmonics in a single run.',
    echoes: 220,
    group: 'Harmonics',
    check: ({ stats }) => n(stats.bestRunFusions) >= 2,
  },
  // One per recipe: the specific, nameable goals a player can steer toward
  // from the pause screen's recipe panel. These are the reason that panel is
  // worth reading — "fuse Static Bloom" is a plan, "get stronger" is not.
  ...FUSION_DEFS.map((def) => ({
    id: `fusion:${def.id}`,
    name: def.name,
    hint: `Fuse ${def.parents[0]} with ${def.parents[1]}.`,
    echoes: 75,
    group: 'Harmonics',
    check: ({ stats }) => (stats.fusionsAchieved ?? []).includes(def.id),
  })),

  // --- pacts and risk -----------------------------------------------------
  {
    id: 'firstPact',
    name: 'Terms Accepted',
    hint: 'Take a pact.',
    echoes: 40,
    group: 'Bargains',
    check: ({ stats }) => n(stats.totalPactsTaken) >= 1,
  },
  {
    id: 'twoPacts',
    name: 'Double Bind',
    hint: 'Win a run carrying two pacts.',
    echoes: 180,
    group: 'Bargains',
    check: inRun((run) => run.won && n(run.pactsTaken) >= 2),
  },
  {
    id: 'pactBoss',
    name: 'Price Paid',
    hint: 'Defeat five bosses in one run while carrying a pact.',
    echoes: 130,
    group: 'Bargains',
    check: inRun((run) => n(run.pactsTaken) >= 1 && n(run.bossesDefeated) >= 5),
  },

  // --- the gauntlet -------------------------------------------------------
  ...ladder({
    idPrefix: 'bosses',
    name: 'Silencer',
    hint: (t) => `Defeat ${t} bosses across all runs.`,
    group: 'The Gauntlet',
    field: 'bossesDefeated',
    tiers: [[10, 30], [50, 60], [150, 120], [500, 240]],
  }),
  // One per boss. The most literal breadcrumbs in the list: after any run, the
  // player can see exactly which named thing they have not yet beaten.
  ...BOSSES.map((boss) => ({
    id: `boss:${boss.spriteKey}`,
    name: `Unmake: ${boss.name}`,
    hint: `Defeat ${boss.name}.`,
    echoes: 60,
    group: 'The Gauntlet',
    check: ({ stats }) => (stats.defeatedBosses ?? []).includes(boss.spriteKey),
  })),
  {
    id: 'flawlessAct',
    name: 'Clean Sweep',
    hint: 'Clear the gauntlet without dying once.',
    echoes: 250,
    group: 'The Gauntlet',
    check: inRun((run) => run.won && !run.died),
  },
  ...ladder({
    idPrefix: 'wins',
    name: 'Curtain Call',
    hint: (t) => `Clear the gauntlet ${t} times.`,
    group: 'The Gauntlet',
    field: 'wins',
    tiers: [[3, 60], [10, 150], [25, 300]],
  }),

  // --- the later acts -----------------------------------------------------
  // The per-boss breadcrumbs used to stop at the Unmade King, so twelve of the
  // nineteen campaign bosses had none — while the act-agnostic entries above
  // (`wins`, Clean Sweep) counted an Undersong or Anthem clear perfectly well.
  // Two thirds of the campaign was paying into the ladder achievements and
  // showing up in none of the literal ones (ACT-PARITY A3).
  //
  // `actId` is the spoiler gate: these name bosses in acts the player may not
  // have unlocked, and a hint is visible while an achievement is locked. The
  // achievements list filters on it (visibleAchievements) exactly as Boss
  // Challenge and Card Practice do. Payout rises per act, matching the fact
  // that a later act's boss is a longer, harder kill.
  ...ACT2_BOSSES.map((boss) => ({
    id: `boss:${boss.spriteKey}`,
    name: `Unmake: ${boss.name}`,
    hint: `Defeat ${boss.name}.`,
    echoes: 80,
    group: 'The Undersong',
    actId: 'act2',
    check: ({ stats }) => (stats.defeatedBosses ?? []).includes(boss.spriteKey),
  })),
  {
    id: 'actClear:act2',
    name: 'The Deep Quieted',
    hint: 'Clear Act II.',
    echoes: 300,
    group: 'The Undersong',
    actId: 'act2',
    check: ({ stats }) => (stats.clearedActs ?? []).includes('act2'),
  },
  ...ACT3_BOSSES.map((boss) => ({
    id: `boss:${boss.spriteKey}`,
    name: `Unmake: ${boss.name}`,
    hint: `Defeat ${boss.name}.`,
    echoes: 100,
    group: 'The Anthem',
    actId: 'act3',
    check: ({ stats }) => (stats.defeatedBosses ?? []).includes(boss.spriteKey),
  })),
  {
    id: 'actClear:act3',
    name: 'Nothing Left To Play',
    hint: 'Clear Act III.',
    echoes: 400,
    group: 'The Anthem',
    actId: 'act3',
    check: ({ stats }) => (stats.clearedActs ?? []).includes('act3'),
  },

  // --- endless and ascension ----------------------------------------------
  {
    id: 'endlessFirst',
    name: 'No Last Verse',
    hint: 'Continue into an endless loop.',
    echoes: 60,
    group: 'Beyond',
    check: ({ stats }) => n(stats.endlessRuns) >= 1,
  },
  ...ladder({
    idPrefix: 'ascension',
    name: 'Ascension',
    hint: (t) => `Win at Ascension ${t} or higher.`,
    group: 'Beyond',
    field: 'highestAscension',
    tiers: [[1, 60], [2, 100], [3, 150], [4, 220], [5, 320]],
  }),
  {
    id: 'echoDuelFirst',
    name: 'Know Thyself',
    hint: 'Win an Echo Duel against a past run.',
    echoes: 80,
    group: 'Beyond',
    check: ({ stats }) => n(stats.totalEchoDuelsWon) >= 1,
  },
  ...ladder({
    idPrefix: 'echoDuels',
    name: 'Self-Possessed',
    hint: (t) => `Win ${t} Echo Duels.`,
    group: 'Beyond',
    field: 'totalEchoDuelsWon',
    tiers: [[5, 100], [20, 200]],
  }),

  // --- champions ----------------------------------------------------------
  // Every champion gets one. Rotating champions is the cheapest variety the
  // game already has and nothing was previously asking players to do it.
  ...CHARACTERS.map((character) => ({
    id: `champion:${character.id}`,
    name: `Voice of ${character.name}`,
    hint: `Defeat three bosses in one run as ${character.name}.`,
    echoes: 50,
    group: 'Champions',
    check: ({ stats }) => n((stats.bossesByCharacter ?? {})[character.id]) >= 3,
  })),

  // --- attrition ----------------------------------------------------------
  ...ladder({
    idPrefix: 'damage',
    name: 'Output',
    hint: (t) => `Deal ${t.toLocaleString()} lifetime damage.`,
    group: 'Attrition',
    field: 'totalDamageDealt',
    tiers: [[100000, 30], [1000000, 60], [10000000, 120], [100000000, 240]],
  }),
  ...ladder({
    idPrefix: 'survival',
    name: 'Long Song',
    hint: (t) => `Survive ${Math.round(t / 60).toLocaleString()} minutes in total.`,
    group: 'Attrition',
    field: 'totalSurvivalSeconds',
    tiers: [[3600, 30], [18000, 60], [72000, 120]],
  }),
  ...ladder({
    idPrefix: 'runs',
    name: 'Persistence',
    hint: (t) => `Play ${t} runs.`,
    group: 'Attrition',
    field: 'runs',
    tiers: [[10, 20], [50, 50], [200, 120], [1000, 300]],
  }),
  {
    id: 'levels40',
    name: 'Crescendo',
    hint: 'Reach level 40 in one run.',
    echoes: 100,
    group: 'Attrition',
    check: ({ stats }) => n(stats.bestRunLevel) >= 40,
  },
  {
    id: 'levels60',
    name: 'Fortissimo',
    hint: 'Reach level 60 in one run.',
    echoes: 200,
    group: 'Attrition',
    check: ({ stats }) => n(stats.bestRunLevel) >= 60,
  },

  // --- spoils (BULLET-HEAVEN.md §H3) --------------------------------------
  ...ladder({
    idPrefix: 'chests',
    name: 'Spoils',
    hint: (t) => `Open ${t} Spoils chests.`,
    group: 'Spoils',
    field: 'totalChestsOpened',
    tiers: [[10, 20], [50, 50], [200, 120]],
  }),
  {
    id: 'jackpot',
    name: 'Jackpot',
    hint: 'Open a five-reward Spoils chest after the run\'s opening three.',
    echoes: 90,
    group: 'Spoils',
    check: ({ stats }) => n(stats.bestLateChest) >= 5,
  },
];

/** Fast lookup, and the guard against a duplicated id silently shadowing one. */
export const ACHIEVEMENTS_BY_ID = new Map(ACHIEVEMENTS.map((a) => [a.id, a]));

/**
 * The achievements a given save is allowed to be shown — everything except
 * entries carrying an `actId` for an act it hasn't unlocked.
 *
 * Only the LIST is filtered, never the evaluation: a hidden achievement still
 * unlocks and still pays the moment its condition holds (satisfiedAchievements
 * reads the whole array), it simply isn't advertised beforehand. A locked row
 * already hides its name, but it shows its hint, and "Defeat The Anthem." is
 * the whole spoiler.
 */
export function visibleAchievements(statistics) {
  return ACHIEVEMENTS.filter((a) => {
    if (!a.actId) return true;
    const act = ACTS.find((x) => x.id === a.actId);
    return !act || isActUnlocked(act, statistics);
  });
}

/**
 * Every achievement whose condition now holds, given lifetime stats and an
 * optional just-finished run. Pure — persisting the result is SaveManager's
 * job (see `evaluateAchievements`), so this stays trivially testable.
 */
export function satisfiedAchievements({ stats, run = null }) {
  const ctx = { stats: stats ?? {}, run };
  return ACHIEVEMENTS.filter((achievement) => {
    try {
      return Boolean(achievement.check(ctx));
    } catch {
      // A malformed old save must never take the run-end screen down with it.
      // A check that throws is simply not satisfied yet.
      return false;
    }
  });
}
